import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { useProductDetailContext } from "../../context/ProductDetailContext";

import SubProductItemForm from "./ItemForm";

import FormDialog from "@/components/reusables/FormDialog";
import ButtonWithIcon from "@/components/ui/ButtonWithIcon";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Button_Names } from "@/constants/global-constants";
import { ToolsConstants, ToolsKeys } from "@/constants/tools.constant";
import useQueryID from "@/hooks/useQueryId";
import { cn } from "@/lib/utils";
import { useMutateSubProductItemMutation } from "@/redux/api/toolsQuery";
import {
  TAddSubProductItem,
  createSubProductItemSchema,
  defaultSubProductItem,
  emptySubProductItems,
} from "@/schemas/tools.schema";
import { customToast } from "@/utils/custom-toast";
import {
  filterEmptyValues,
  parseToFormData,
  validateResponse,
} from "@/utils/helper-functions/form.helper";

interface AddSubProductItemModalProps {
  isOpen: boolean;
  toggleAddSubProductItemModal: () => void;
}

const AddSubProductItemModal = ({
  isOpen,
  toggleAddSubProductItemModal,
}: AddSubProductItemModalProps): JSX.Element => {
  const { productId } = useProductDetailContext();
  const { id: subProductId } = useQueryID(ToolsKeys.SUB_PRODUCT_ID);
  const [mutateSubProductItem, { isLoading }] =
    useMutateSubProductItemMutation();

  const form = useForm<TAddSubProductItem>({
    resolver: zodResolver(createSubProductItemSchema),
    defaultValues: defaultSubProductItem,
  });

  const isForBulkUpload = form.watch("isBulkUpload");

  const handleBulkUpload = (): void => {
    form.setValue("isBulkUpload", true);
    form.setValue("items", [
      ...(form.getValues("items") || []),
      emptySubProductItems,
      emptySubProductItems,
      emptySubProductItems,
      emptySubProductItems,
    ]);
    //Reset the default values
    form.reset({
      isBulkUpload: true,
      items: [
        emptySubProductItems,
        emptySubProductItems,
        emptySubProductItems,
        emptySubProductItems,
      ],
    });
  };

  const handleExit = (): void => {
    toggleAddSubProductItemModal();
  };

  const onSubmit = async (values: TAddSubProductItem): Promise<void> => {
    const formattedData = {
      items: values?.items?.map((item) => ({
        ...item,
        images: [],
      })),
    };
    const dataToSend = parseToFormData(filterEmptyValues(formattedData), [
      "images",
    ]);

    values?.items?.forEach((item, index) => {
      if (item?.images?.length) {
        item?.images?.forEach((image) => {
          dataToSend.append(`items.${index}.images`, image as File);
        });
      }
    });

    const response = await mutateSubProductItem({
      method: "POST",
      data: dataToSend,
      productId,
      subProductId,
    });

    validateResponse({
      response,
      onSuccess: () => {
        handleExit();
        customToast.success(ToolsConstants.FORM.ITEMS.ADDED_ITEMS_SUCCESS);
      },
    });
  };

  return (
    <FormDialog
      isOpen={isOpen}
      title={ToolsConstants.FORM.ITEMS.ADD_ITEM}
      toggleModal={handleExit}
      className={cn(isForBulkUpload ? "md:max-w-5xl" : "px-2")}
      isFormDirty={form.formState.isDirty}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-8'>
          <SubProductItemForm />
          <section
            className={cn(isForBulkUpload ? "" : " flex justify-between")}
          >
            {!isForBulkUpload && (
              <div>
                <ButtonWithIcon variant={"light"} onClick={handleBulkUpload}>
                  {ToolsConstants.FORM.ITEMS.ADD_BULK_ITEMS}
                </ButtonWithIcon>
              </div>
            )}
            <div
              className={cn(
                isForBulkUpload ? "flex justify-between" : "space-x-6",
              )}
            >
              <Button
                variant={"ghost"}
                onClick={() => form.reset()}
                disabled={isLoading}
              >
                {Button_Names.Reset}
              </Button>
              <Button type='submit' className='w-[200px]' isLoading={isLoading}>
                {ToolsConstants.FORM.ITEMS.SAVE_ITEM}
              </Button>
            </div>
          </section>
        </form>
      </Form>
    </FormDialog>
  );
};

export default AddSubProductItemModal;
